import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  format,
  differenceInCalendarDays
} from 'date-fns';

export type TrendGranularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

/** Soft cap so trend/forecast charts stay readable instead of painting a stain of dots. */
export const MAX_TREND_CHART_POINTS = 48;

const WEEK_OPTIONS = { weekStartsOn: 1 as const }; // Monday

export interface TrendBucket {
  key: string;
  label: string;
  start: Date;
  end: Date;
  granularity: TrendGranularity;
}

/**
 * Pick the coarsest time grain that keeps chart points under the readable limit.
 */
export const chooseTrendGranularity = (dates: Date[]): TrendGranularity => {
  if (dates.length === 0) return 'day';
  if (dates.length <= MAX_TREND_CHART_POINTS) return 'day';

  const times = dates.map(d => d.getTime());
  const min = Math.min(...times);
  const max = Math.max(...times);
  const spanDays = Math.max(1, differenceInCalendarDays(new Date(max), new Date(min)));

  if (Math.ceil(spanDays / 7) <= MAX_TREND_CHART_POINTS) return 'week';
  if (Math.ceil(spanDays / 30) <= MAX_TREND_CHART_POINTS) return 'month';
  if (Math.ceil(spanDays / 90) <= MAX_TREND_CHART_POINTS) return 'quarter';
  return 'year';
};

export const getTrendBucket = (date: Date, granularity: TrendGranularity): TrendBucket => {
  switch (granularity) {
    case 'week': {
      const start = startOfWeek(date, WEEK_OPTIONS);
      const end = endOfWeek(date, WEEK_OPTIONS);
      return {
        key: format(start, 'RRRR-\'W\'II'),
        label: `Wk of ${format(start, 'dd/MM/yyyy')}`,
        start,
        end,
        granularity
      };
    }
    case 'month': {
      const start = startOfMonth(date);
      const end = endOfMonth(date);
      return {
        key: format(start, 'yyyy-MM'),
        label: format(start, 'MMM yyyy'),
        start,
        end,
        granularity
      };
    }
    case 'quarter': {
      const start = startOfQuarter(date);
      const end = endOfQuarter(date);
      return {
        key: format(start, 'yyyy-\'Q\'Q'),
        label: format(start, 'QQQ yyyy'),
        start,
        end,
        granularity
      };
    }
    case 'year': {
      const start = startOfYear(date);
      const end = endOfYear(date);
      return {
        key: format(start, 'yyyy'),
        label: format(start, 'yyyy'),
        start,
        end,
        granularity
      };
    }
    case 'day':
    default: {
      const start = startOfDay(date);
      const end = endOfDay(date);
      return {
        key: format(start, 'yyyy-MM-dd'),
        label: format(start, 'dd/MM/yyyy'),
        start,
        end,
        granularity: 'day'
      };
    }
  }
};

export const describeTrendGranularity = (granularity: TrendGranularity): string => {
  switch (granularity) {
    case 'week':
      return 'weekly';
    case 'month':
      return 'monthly';
    case 'quarter':
      return 'quarterly';
    case 'year':
      return 'yearly';
    case 'day':
    default:
      return 'daily';
  }
};

/**
 * True when a parsed date falls inside an aggregated chart bucket.
 */
export const isDateInTrendBucket = (
  date: Date,
  bucketStart?: Date,
  bucketEnd?: Date
): boolean => {
  if (!bucketStart || !bucketEnd) return false;
  const t = date.getTime();
  return t >= bucketStart.getTime() && t <= bucketEnd.getTime();
};
